import React, { useEffect, useRef, useState } from "react";
import { Camera, X, ImageUp } from "lucide-react";

// Live camera with a strip-shaped outline so every strip photo is framed the same way.
// Crops to the outline (plus a white margin for colour balance). Falls back to the phone camera app.
const BOX = { w: 0.26, h: 0.78 }; // outline as a share of the preview width/height

export default function StripCamera({ brand, pads = 6, onShot, onClose, onFallback }) {
  const vid = useRef(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => { stream = s; if (vid.current) { vid.current.srcObject = s; vid.current.play().catch(() => {}); } })
      .catch(() => setErr("Camera not available"));
    return () => stream && stream.getTracks().forEach((t) => t.stop());
  }, []);
  useEffect(() => { if (!navigator.mediaDevices) setErr("Camera not available"); }, []);

  const shoot = () => {
    const v = vid.current; if (!v || !v.videoWidth) return;
    const el = v.getBoundingClientRect(), vw = v.videoWidth, vh = v.videoHeight;
    const scale = Math.max(el.width / vw, el.height / vh); // object-cover mapping
    const ox = (vw * scale - el.width) / 2, oy = (vh * scale - el.height) / 2;
    const bw = el.width * BOX.w * 1.6, bh = el.height * BOX.h * 1.06; // keep some white around the strip
    const sx = (el.width / 2 - bw / 2 + ox) / scale, sy = (el.height / 2 - bh / 2 + oy) / scale;
    const c = document.createElement("canvas"); c.width = Math.round(bw / scale); c.height = Math.round(bh / scale);
    c.getContext("2d").drawImage(v, sx, sy, c.width, c.height, 0, 0, c.width, c.height);
    c.toBlob((b) => b && onShot(new File([b], "strip.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black flex flex-col" data-testid="tech-strip-camera">
      <div className="relative flex-1 overflow-hidden">
        <video ref={vid} playsInline muted className="absolute inset-0 h-full w-full object-cover" data-testid="tech-strip-video" />
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative rounded-lg border-[3px] border-dashed border-yellow-300 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" style={{ width: `${BOX.w * 100}%`, height: `${BOX.h * 100}%` }} data-testid="tech-strip-outline">
            {Array.from({ length: pads }, (_, i) => <div key={i} className="absolute left-1/2 -translate-x-1/2 border-2 border-yellow-200/70 rounded-sm" style={{ top: `${3 + i * (62 / pads)}%`, height: `${62 / pads - 2.5}%`, aspectRatio: "1 / 1", maxWidth: "75%" }} />)}
            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[13px] font-black text-yellow-200">HANDLE THIS END</div>
          </div>
        </div>
        <div className="absolute top-0 inset-x-0 p-3 bg-gradient-to-b from-black/80 to-transparent text-white">
          <div className="text-[18px] font-black">{brand} strip</div>
          <div className="text-[14px] font-bold text-white/85">Lay the strip on white paper, pads up. Fit it inside the yellow box, handle at the bottom. No shadow, no flash.</div>
        </div>
        <button onClick={onClose} data-testid="tech-strip-cam-close" className="absolute top-3 right-3 h-11 w-11 rounded-full bg-white/20 text-white flex items-center justify-center"><X className="h-6 w-6" /></button>
        {err && <div className="absolute inset-x-4 top-1/3 bg-white rounded-2xl p-4 text-center space-y-3" data-testid="tech-strip-cam-error">
          <div className="text-[17px] font-black text-slate-800">{err} — use the phone camera instead</div>
          <button onClick={onFallback} className="w-full h-12 rounded-xl bg-cyan-600 text-white text-[17px] font-black">Open phone camera</button>
        </div>}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center px-6 py-4 bg-black">
        <button onClick={onFallback} data-testid="tech-strip-cam-fallback" className="text-white/80 text-[13px] font-bold flex flex-col items-start gap-1"><ImageUp className="h-6 w-6" />Phone camera</button>
        <button onClick={shoot} data-testid="tech-strip-shoot" aria-label="Take photo" className="h-20 w-20 rounded-full bg-white border-[6px] border-yellow-300 flex items-center justify-center active:scale-95"><Camera className="h-8 w-8 text-slate-800" /></button>
        <span />
      </div>
    </div>
  );
}
